import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays,
  MapPin,
  Trophy,
  Target,
  Camera,
  LogIn,
  Mail,
  Phone,
  Users,
  ArrowRight,
  Sparkles,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso, withTime = false) {
  if (!iso) return "—";
  const d = new Date(iso);
  const opts = withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" };
  return d.toLocaleDateString("fr-FR", opts);
}

function TypeBadge({ type, label }) {
  const map = {
    internal: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    official: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    gala: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  const cls = map[type] || "bg-slate-700/40 text-slate-300 border-slate-600";
  return (
    <span
      data-testid={`match-type-${type}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-mono-stat uppercase tracking-widest border ${cls}`}
    >
      {label}
    </span>
  );
}

function ScoreBlock({ score }) {
  const h = score.home;
  const a = score.away;
  const draw = h != null && a != null && h === a;
  const homeWin = h != null && a != null && h > a;
  const awayWin = h != null && a != null && h < a;
  return (
    <div className="flex items-center gap-3 justify-center text-center">
      <div className="min-w-[90px]">
        <div className="text-[10px] font-mono-stat text-slate-400 uppercase truncate">{score.home_label}</div>
        <div className={`text-3xl font-black ${homeWin ? "text-[#E5B85C]" : "text-slate-200"}`}>
          {h ?? "—"}
        </div>
      </div>
      <div className="text-slate-600 font-black text-xl">:</div>
      <div className="min-w-[90px]">
        <div className="text-[10px] font-mono-stat text-slate-400 uppercase truncate">{score.away_label}</div>
        <div className={`text-3xl font-black ${awayWin ? "text-[#E5B85C]" : "text-slate-200"}`}>
          {a ?? "—"}
        </div>
      </div>
      {draw && h != null && (
        <div className="absolute mt-14 text-[9px] font-mono-stat text-slate-500 uppercase tracking-widest">
          Match nul
        </div>
      )}
    </div>
  );
}

function MatchCard({ m, onPhoto }) {
  return (
    <div
      data-testid={`public-match-${m.id}`}
      className="rounded-2xl bg-slate-900/80 border border-white/10 overflow-hidden hover:border-[#E5B85C]/40 transition-colors"
    >
      <div className="p-4 flex items-center justify-between gap-2 border-b border-white/5">
        <div className="flex items-center gap-2 min-w-0">
          <TypeBadge type={m.match_type} label={m.match_type_label} />
          <div className="text-xs text-slate-400 truncate">
            <CalendarDays className="w-3 h-3 inline mr-1" />
            {fmtDate(m.match_date)}
            {m.location ? (
              <>
                <span className="mx-1.5">·</span>
                <MapPin className="w-3 h-3 inline mr-1" />
                {m.location}
              </>
            ) : null}
          </div>
        </div>
        {m.has_photo && (
          <button
            data-testid={`public-match-photo-${m.id}`}
            onClick={() => onPhoto(m)}
            className="inline-flex items-center gap-1 text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C] hover:text-[#F3D080]"
          >
            <Camera className="w-3 h-3" /> Photo
          </button>
        )}
      </div>
      <div className="p-5">
        <div className="font-display font-bold text-sm uppercase tracking-wide text-slate-200 mb-3 text-center truncate">
          {m.title}
        </div>
        <ScoreBlock score={m.score} />
      </div>
      {(m.scorers?.length > 0 || m.assists?.length > 0 || m.motm_name) && (
        <div className="px-5 pb-4 pt-2 border-t border-white/5 space-y-2">
          {m.motm_name && (
            <div className="text-[11px]">
              <span className="font-mono-stat uppercase tracking-widest text-[#E5B85C] mr-2">
                <Sparkles className="w-3 h-3 inline" /> MOTM
              </span>
              <span className="text-slate-200 font-semibold">{m.motm_name}</span>
            </div>
          )}
          {m.scorers?.length > 0 && (
            <div className="text-[11px]">
              <span className="font-mono-stat uppercase tracking-widest text-emerald-300 mr-2">
                <Target className="w-3 h-3 inline" /> Buts
              </span>
              <span className="text-slate-300">
                {m.scorers.map((s, i) => (
                  <span key={i}>
                    {s.name}
                    {s.goals > 1 ? ` (${s.goals})` : ""}
                    {i < m.scorers.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </div>
          )}
          {m.assists?.length > 0 && (
            <div className="text-[11px]">
              <span className="font-mono-stat uppercase tracking-widest text-sky-300 mr-2">
                Assists
              </span>
              <span className="text-slate-300">
                {m.assists.map((s, i) => (
                  <span key={i}>
                    {s.name}
                    {s.assists > 1 ? ` (${s.assists})` : ""}
                    {i < m.assists.length - 1 ? ", " : ""}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoModal({ match, onClose }) {
  if (!match) return null;
  return (
    <div
      data-testid="public-photo-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="public-photo-close"
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white hover:text-[#E5B85C]"
          aria-label="Fermer"
        >
          <X className="w-6 h-6" />
        </button>
        <img
          src={`${API}/public/photo/${match.match_id || match.id}`}
          alt={match.title || "Photo"}
          className="w-full rounded-xl shadow-2xl shadow-black/60"
        />
        <div className="mt-3 text-center">
          <div className="font-display font-bold text-white">{match.title || "Match"}</div>
          <div className="text-xs text-slate-400 mt-1">{fmtDate(match.match_date)}</div>
        </div>
      </div>
    </div>
  );
}

export default function ClubHome() {
  const [club, setClub] = useState({ name: "", logo_url: "/club-logo.png" });
  const [matches, setMatches] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [nextMatch, setNextMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, mm, lb, ph, nx] = await Promise.all([
          axios.get(`${API}/public/club`),
          axios.get(`${API}/public/matches?limit=10`),
          axios.get(`${API}/public/leaderboard?limit=10`),
          axios.get(`${API}/public/photos?limit=18`),
          axios.get(`${API}/public/next-match`),
        ]);
        setClub(c.data);
        setMatches(mm.data.matches || []);
        setLeaderboard(lb.data.leaderboard || []);
        setPhotos(ph.data.photos || []);
        setNextMatch(nx.data.match);
      } catch (e) {
        // keep page usable even if API hiccups
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0B0F17]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={club.logo_url || "/club-logo.png"}
              alt="Logo"
              className="w-9 h-9 rounded-full object-cover border border-[#E5B85C]/50"
            />
            <div className="min-w-0">
              <div className="text-[9px] font-mono-stat uppercase tracking-widest text-slate-500">
                Interface invité
              </div>
              <div className="font-display font-bold uppercase text-sm truncate">
                {club.name || "Club"}
              </div>
            </div>
          </div>
          <Link
            to="/login"
            data-testid="public-login-link"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-[11px] font-bold uppercase tracking-wide"
          >
            <LogIn className="w-3.5 h-3.5" /> Se connecter
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-10">
        {/* Hero */}
        <section data-testid="public-hero" className="text-center space-y-4 py-6">
          <img
            src={club.logo_url || "/club-logo.png"}
            alt={club.name}
            className="w-28 h-28 mx-auto rounded-full object-cover border-2 border-[#E5B85C]/60 shadow-lg shadow-[#E5B85C]/10"
          />
          <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight">
            {club.name || "Notre club"}
          </h1>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto">
            {club.about ||
              "Suivez les résultats, les photos et les statistiques du club. Les familles, supporters et curieux sont les bienvenus sur cet espace public."}
          </p>
          {(club.contact_email || club.contact_phone || club.location) && (
            <div className="flex items-center justify-center gap-5 flex-wrap text-xs text-slate-400 pt-2">
              {club.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#E5B85C]" />
                  {club.location}
                </span>
              )}
              {club.contact_email && (
                <a
                  href={`mailto:${club.contact_email}`}
                  className="inline-flex items-center gap-1.5 hover:text-[#E5B85C]"
                >
                  <Mail className="w-3.5 h-3.5 text-[#E5B85C]" />
                  {club.contact_email}
                </a>
              )}
              {club.contact_phone && (
                <a
                  href={`tel:${club.contact_phone}`}
                  className="inline-flex items-center gap-1.5 hover:text-[#E5B85C]"
                >
                  <Phone className="w-3.5 h-3.5 text-[#E5B85C]" />
                  {club.contact_phone}
                </a>
              )}
            </div>
          )}
        </section>

        {/* Next match */}
        {nextMatch && (
          <section data-testid="public-next-match" className="rounded-2xl bg-gradient-to-br from-[#E5B85C]/15 via-slate-900 to-slate-900 border border-[#E5B85C]/30 p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div className="font-display font-bold text-lg uppercase text-[#E5B85C] flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> Prochain match
              </div>
              <TypeBadge type={nextMatch.match_type} label={nextMatch.match_type_label} />
            </div>
            <div className="text-2xl font-display font-bold text-white mb-2">{nextMatch.title}</div>
            <div className="text-sm text-slate-300 flex flex-wrap gap-4">
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" /> {fmtDate(nextMatch.match_date, true)}
              </span>
              {nextMatch.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> {nextMatch.location}
                </span>
              )}
            </div>
          </section>
        )}

        {/* Results */}
        <section data-testid="public-results">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#E5B85C]" />
              Résultats récents
            </h2>
            <span className="text-xs text-slate-500 font-mono-stat uppercase tracking-widest">
              {matches.length} match{matches.length > 1 ? "s" : ""}
            </span>
          </div>
          {loading && <div className="text-slate-500 text-sm py-10 text-center">Chargement...</div>}
          {!loading && matches.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center rounded-2xl bg-slate-900/60 border border-white/5">
              Aucun match terminé pour l'instant.
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {matches.map((m) => (
              <MatchCard key={m.id} m={m} onPhoto={(mm) => setModal(mm)} />
            ))}
          </div>
        </section>

        {/* Leaderboard */}
        <section data-testid="public-leaderboard">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#E5B85C]" />
            Top buteurs & passeurs
          </h2>
          <div className="rounded-2xl bg-slate-900/80 border border-white/10 overflow-hidden">
            {leaderboard.length === 0 && (
              <div className="text-slate-500 text-sm py-10 text-center">
                Aucune statistique pour l'instant.
              </div>
            )}
            {leaderboard.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="text-left py-2.5 px-4 w-10">#</th>
                    <th className="text-left py-2.5 px-4">Joueur</th>
                    <th className="text-right py-2.5 px-4">Buts</th>
                    <th className="text-right py-2.5 px-4">Assists</th>
                    <th className="text-right py-2.5 px-4">MJ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {leaderboard.map((p, i) => (
                    <tr key={i} data-testid={`public-lb-${i}`}>
                      <td className="py-2.5 px-4 font-mono-stat text-slate-500">{i + 1}</td>
                      <td className="py-2.5 px-4 font-semibold">{p.player_name}</td>
                      <td className="py-2.5 px-4 text-right font-mono-stat text-[#E5B85C]">
                        {p.goals}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono-stat text-sky-300">
                        {p.assists}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono-stat text-slate-400">
                        {p.matches_played}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Photos */}
        <section data-testid="public-photos">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-[#E5B85C]" />
            Galerie du club
          </h2>
          {photos.length === 0 && (
            <div className="text-slate-500 text-sm py-10 text-center rounded-2xl bg-slate-900/60 border border-white/5">
              Aucune photo pour l'instant.
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p) => (
              <button
                key={p.match_id}
                data-testid={`public-photo-${p.match_id}`}
                onClick={() => setModal(p)}
                className="group relative rounded-xl overflow-hidden bg-slate-900 border border-white/10 hover:border-[#E5B85C]/50 transition-all aspect-square"
              >
                <img
                  src={`${API}/public/photo/${p.match_id}`}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                  <TypeBadge type={p.match_type} label={p.match_type_label} />
                  <div className="text-xs font-bold text-white mt-1 truncate">{p.title}</div>
                  <div className="text-[10px] text-slate-300">{fmtDate(p.match_date)}</div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="text-center py-10 border-t border-white/10">
          <div className="text-sm text-slate-400 mb-3">
            Vous faites partie de l'équipe ?
          </div>
          <Link
            to="/login"
            data-testid="public-footer-login"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide"
          >
            Accéder à l'espace joueur <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <PhotoModal match={modal} onClose={() => setModal(null)} />
    </div>
  );
}
